import { action, observable, computed } from 'mobx';
import type { RootStore } from '~/stores/root.store';
import {
    TFlowPanel,
    FlowPanel,
    TRequestStatus,
    ICreateFlowData,
    TFlowActionType,
    IFlowFilterByAction,
    TProjectListFilters,
    TEntryPoint
} from '~/components/CreateFlow/types/create-flow.types';
import { IFileTemplate, IFlowTemplate, IFolder } from '~/types/model.types';
import {
    AtoZcomparator,
    ISODateStringComparator
} from '~/utils/automation.utils';
import { TSortBy } from '~/components/Templates/types/templates.types';
import { IFlowGalleryTemplate } from '~/components/FlowGallery/types/flow-gallery.types';

/*
    Notes:
    isProjectScopedFlow = true when flow creation is within a 
    workspace/Project. Else is creation from top nav.
*/
export class CreateFlowUiStore {
    @observable eventName: string;
    @observable workspaceId: string = null;
    @observable activeFlowPanel: TFlowPanel = 'Most_used_templates';
    @observable activeFolderPanel: IFolder = null;
    @observable flowTemplates: IFlowTemplate[] = [];
    @observable mostUsedTemplates: IFlowTemplate[] = [];
    @observable maxFlowsToDisplay: number = 9;
    @observable recentlySentFlows: IFlowTemplate[] = [];
    @observable recentlySentLegacyFiles: IFileTemplate[] = [];
    @observable suggestedTemplates: IFlowGalleryTemplate[] = [];
    @observable folders: IFolder[] = [];
    @observable requestStatus: TRequestStatus = 'fetching';
    @observable isProjectScopedFlow: boolean = false;
    @observable isFilterByInvoice: boolean = false;
    @observable isFilterByContract: boolean = false;
    @observable isFilterByQuestion: boolean = false;
    @observable isFilterByServices: boolean = false;
    @observable isFilterByScheduler: boolean = false;
    @observable isFilterByRecurringInvoice: boolean = false;
    @observable isGridView: boolean = false;
    @observable closeModal: Function;
    @observable projectListFilters: TProjectListFilters;
    @observable isQuickPreviewOpen: boolean = false;
    @observable sortBy: TSortBy = 'date-desc';
    @observable isFoldersLoading: boolean = true;
    @observable initialFilters: TFlowActionType[] = [];
    @observable clientId: string;
    @observable source: string;
    @observable isNavBarScoped: boolean;
    @observable entryPoint: TEntryPoint;
    rootStore: RootStore = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @action setRequestStatus(status: TRequestStatus) {
        this.requestStatus = status;
    }

    @action setLayoutViewMode(val: boolean) {
        this.isGridView = val;
    }

    @action loadMostUsedTemplates(templates: IFlowTemplate[]) {
        this.mostUsedTemplates = templates;
    }

    @action loadRecentlySentFlows(templates: IFlowTemplate[]) {
        this.recentlySentFlows = templates;
    }

    @action loadRecentlySentLegacyFiles(templates: IFileTemplate[]) {
        this.recentlySentLegacyFiles = templates;
    }

    @action setOnCloseModal(closeFunc: Function) {
        this.closeModal = closeFunc;
    }

    @action setProjectListFilters(projectListFilters: TProjectListFilters) {
        this.projectListFilters = projectListFilters;
    }

    @action setClient(clientId: string) {
        this.clientId = clientId;
    }

    @action setNavBarScoped(isNavBarScoped: boolean) {
        this.isNavBarScoped = isNavBarScoped;
    }

    @action setSource(source: string) {
        this.source = source;
    }

    @action setEntryPoint(entryPoint: TEntryPoint) {
        this.entryPoint = entryPoint;
    }

    @action loadSuggestedTemplates(templates: IFlowGalleryTemplate[]) {
        this.suggestedTemplates = templates;
    }

    @action loadFlowTemplates(templates: IFlowTemplate[]) {
        this.flowTemplates = templates;
    }

    @action loadFolders(folders: IFolder[]) {
        this.folders = folders;
        this.isFoldersLoading = false;
    }

    @action setActiveFlowPanel(panel: TFlowPanel) {
        this.activeFolderPanel = null;
        this.activeFlowPanel = panel;
    }

    @action setActiveFolderPanel(folder: IFolder) {
        this.activeFlowPanel = null;
        this.activeFolderPanel = folder;
    }

    @action setProject(
        project: Pick<ICreateFlowData, 'event_name' | 'workspace_id'>
    ) {
        this.eventName = project.event_name;
        this.workspaceId = project.workspace_id;
        this.isProjectScopedFlow = true;
    }

    @action.bound setInitialFilters(actions: TFlowActionType[]) {
        this.initialFilters = actions;
    }

    @action.bound
    setFlowFilterType(filterKey: TFlowActionType) {
        switch (filterKey) {
            case 'questionnaire':
                this.isFilterByQuestion = !this.isFilterByQuestion;
                break;
            case 'contract':
                this.isFilterByContract = !this.isFilterByContract;
                break;
            case 'invoice':
                this.isFilterByInvoice = !this.isFilterByInvoice;
                break;
            case 'services':
                this.isFilterByServices = !this.isFilterByServices;
                break;
            case 'scheduler':
                this.isFilterByScheduler = !this.isFilterByScheduler;
                break;
            case 'recurring_invoice':
                this.isFilterByRecurringInvoice =
                    !this.isFilterByRecurringInvoice;
                break;
            default:
                throw Error('Invalid filter key');
        }
    }

    @action.bound
    loadFlowFilterTypes(filters: TFlowActionType[]) {
        filters.forEach(item => this.setFlowFilterType(item));
    }

    @action.bound
    setSortBy(sortBy: TSortBy) {
        this.sortBy = sortBy;
    }

    @action.bound
    setIsQuickPreviewOpen(isOpen: boolean) {
        this.isQuickPreviewOpen = isOpen;
    }

    @computed
    get myFlows() {
        return this.flowTemplates.filter(i => i.folder_id === null);
    }
    @computed
    get sortedFlowTemplates() {
        const templates = this.activePanel.isMyFlows
            ? this.myFlows
            : this.flowTemplates;
        return templates?.slice().sort((a, b) => {
            switch (this.sortBy) {
                case 'name-asc':
                    return AtoZcomparator(a, b);
                case 'date-asc':
                    return ISODateStringComparator(a, b);
                case 'date-desc':
                    return ISODateStringComparator(b, a);
                default:
                    return 0;
            }
        });
    }

    @computed
    get flowFilterTypes() {
        const activeFilters: TFlowActionType[] = [];
        const { companyStore } = this.rootStore;
        const isRecurringInvoiceDiscoverabilityFlagOpen =
            companyStore.currCompany?.isNev;

        const list: IFlowFilterByAction[] = [
            {
                key: 'invoice',
                label: 'Invoice',
                isActive: this.isFilterByInvoice
            },
            ...(isRecurringInvoiceDiscoverabilityFlagOpen
                ? [
                      {
                          key: 'recurring_invoice' as const,
                          label: 'Recurring invoice',
                          isActive: this.isFilterByRecurringInvoice
                      }
                  ]
                : []),
            {
                key: 'contract',
                label: 'Contract',
                isActive: this.isFilterByContract
            },
            {
                key: 'questionnaire',
                label: 'Questions',
                isActive: this.isFilterByQuestion
            },
            {
                key: 'services',
                label: 'Services',
                isActive: this.isFilterByServices
            },
            {
                key: 'scheduler',
                label: 'Scheduler',
                isActive: this.isFilterByScheduler
            }
        ];

        list.forEach(i => {
            if (i.isActive) {
                activeFilters.push(i.key);
                return;
            }
            const index = activeFilters.indexOf(i.key);

            if (index > -1) {
                activeFilters.splice(index, 1);
            }
        });

        return { list, activeFilters };
    }

    @computed
    get status() {
        return {
            isIdle: this.requestStatus === 'idle',
            isInitialLoading: this.requestStatus === 'initial_loading',
            isFiltering: this.requestStatus === 'filtering',
            isFetching: this.requestStatus === 'fetching',
            isCreating: this.requestStatus === 'creating',
            isSuccess: this.requestStatus === 'succeeded',
            isFailed: this.requestStatus === 'failed'
        };
    }

    @computed
    get activePanel() {
        const isActive = (val: TFlowPanel) => this.activeFlowPanel === val;
        return {
            isMostUsedTemplates: isActive(FlowPanel.Most_used_templates),
            isSentFlows: isActive(FlowPanel.Recently_sent_flows),
            isAllFlows: isActive(FlowPanel.All_flow_templates),
            isStarred: isActive(FlowPanel.Starred_templates),
            isSuggested: isActive(FlowPanel.Suggested_templates),
            isMyFlows: isActive(FlowPanel.My_flows),
            isFolder: this.activeFolderPanel !== null
        };
    }

    @computed
    get sortedCompanyFolders() {
        return this.folders.slice().sort((a, b) => a.order - b.order);
    }

    @computed
    get isShowSort() {
        return (
            this.activePanel.isMyFlows ||
            this.activePanel.isAllFlows ||
            this.activePanel.isFolder
        );
    }

    @computed
    get isClientViewScopedFlow() {
        return !this.isNavBarScoped && this.clientId;
    }

    @computed
    get recentlySentFilesFlows() {
        const allRecentlySentFiles = this.recentlySentFlows.concat(
            this.recentlySentLegacyFiles
        );
        return allRecentlySentFiles.sort(
            (a, b) => Date.parse(b.sent_on) - Date.parse(a.sent_on)
        );
    }

    @action reset() {
        this.eventName = null;
        this.workspaceId = null;
        this.flowTemplates = [];
        this.folders = [];
        this.mostUsedTemplates = [];
        this.recentlySentFlows = [];
        this.recentlySentLegacyFiles = [];
        this.suggestedTemplates = [];
        this.requestStatus = 'idle';
        this.activeFlowPanel = 'Most_used_templates';
        this.activeFolderPanel = null;
        this.isProjectScopedFlow = false;
        this.isFilterByInvoice = false;
        this.isFilterByContract = false;
        this.isFilterByQuestion = false;
        this.isFilterByServices = false;
        this.isFilterByScheduler = false;
        this.isFilterByRecurringInvoice = false;
        this.isQuickPreviewOpen = false;
        this.isFoldersLoading = true;
        this.initialFilters = [];
        this.entryPoint = null;
    }
}
